<template>
  <with-gallery :update-watcher="value">
    <div class="goal-events">
      <div
        v-for="event in eventsToShow"
        :id="'event-' + event.id"
        :key="event.id"
        class="goal-event"
      >
        <div class="selector" tabindex="-1"></div>
        <div class="content" v-if="!event.checkin || kr(event.checkin)">
          <div class="goal-event-header d-flex justify-content-between align-items-center">
            <div class="position-relative d-flex align-items-center">
              <event-timeline-pointer
                :type="getTimelinePointerType(event)"
                v-if="getTimelinePointerType(event)"
              />
              <user-short
                class="actor-detail align-self-baseline"
                :truncate-name="true"
                :user-id="event.actorId"
                length="40"
              ></user-short>
              <div class="ml-2">
                <span class="bold" v-no-capture>{{ getDisplayName(event.actorId) }}</span>
                <span class="mr-2" v-if="event.type === GOAL_EVENT_TYPE.KR_CREATED">
                  <template v-if="context === ENTITY_TYPE.KEY_RESULT">
                    created the KR
                  </template>
                  <template v-else>
                    created a new KR - {{ getKrTitle(event.keyResultId) }}
                  </template>
                </span>
                <span class="mr-2" v-else-if="event.type === GOAL_EVENT_TYPE.CLOSED">
                  closed the
                  {{ getEntityName(event) }}
                </span>
                <span class="mr-2" v-else-if="event.type === GOAL_EVENT_TYPE.REOPENED">
                  re-opened the {{ getEntityName(event) }}
                </span>
                <span class="mr-2" v-else-if="event.type === GOAL_EVENT_TYPE.COMMENT">
                  added a comment
                </span>
                <span class="mr-2" v-else-if="event.type === GOAL_EVENT_TYPE.CREATED">
                  created the Goal
                </span>
                <span class="mr-2" v-else-if="event.type === GOAL_EVENT_TYPE.AWARD">
                  gave an award
                </span>
                <ownership-change-event
                  v-else-if="event.type === GOAL_EVENT_TYPE.OWNERSHIP_CHANGED"
                  :event="event"
                  class="mr-2"
                />
                <span class="mr-2" v-else-if="event.type === GOAL_EVENT_TYPE.FIELD_CHANGED">
                  <template v-if="event.field === 'title'">
                    changed the <span class="field-name">title</span> from
                    <span class="field-value">{{ event.changes.old }}</span> to
                    <span class="field-value">{{ event.changes.new }}</span>
                  </template>
                  <template v-else-if="event.field === 'description'">
                    {{ getDescriptionTextChange(event) }}
                  </template>
                  <template v-else-if="event.field === 'checkin_frequency'">
                    changed the checkin frequency to
                    <span class="field-value">{{ event.changes.new }}</span>
                  </template>
                  <template v-else-if="event.field === 'timeline'">
                    changed the timeline to
                    <span class="field-value">{{ event.changes.new }}</span>
                  </template>
                </span>
                <span class="mr-2" v-else>
                  updated a KR
                </span>
                <event-time :event="event" :context="context" />
              </div>
            </div>
            <goal-event-actions
              class="event-action-wrapper"
              :event="event"
              @awarded="$emit('awarded')"
              @reacted="updateReactionSummary({ event, reactionSummary: $event })"
            />
          </div>
          <div class="row no-gutters checkin-info" v-if="isKrEvent(event) && context === 'goal'">
            <div class="row no-gutters position-static kr-info">
              <div class="col-auto entity-icon-container">
                <i class="kr-icon-greyed"></i>
              </div>
              <div class="col kr-title pl-2" :title="getKrTitle(event.keyResultId)" v-no-capture>
                {{ getKrTitle(event.keyResultId) }}
              </div>
            </div>
          </div>
          <checkin-values
            class="check-values event-content"
            :checkin="getCheckinFromEvent(event)"
            :kr="getKrSnapshot(event)"
            v-if="event.checkin"
          />
          <div class="bold event-content" v-if="event.type === GOAL_EVENT_TYPE.CLOSED">
            Outcome:
            <span
              class="variant"
              :data-variant="VARIANT_MAP['outcome-' + event.outcome]"
              data-apply-color
              >{{ getOutcomeLabel(event.outcome) }}</span
            >
          </div>
          <goal-event-with-comment
            :event="event"
            :kr="getKrFromEvent(event)"
            :expanded="event.id === +eventToExpand"
            :mentionable-users="mentionableUsers"
            :hide-reply="hideReply"
            @reacted="updateReactionSummary({ event, reactionSummary: $event })"
            @deleted="onEventDeletion(event)"
            @reply="$emit('reply', event)"
            @comment-updated="onCommentUpdate(event, $event)"
            @checkin-updated="onCheckinUpdate(event, $event)"
            v-if="isCommentedEvent(event.type)"
          />
          <div v-if="isGoalAward(event)" class="d-flex align-items-center">
            <div class="col-auto entity-icon-container">
              <i class="goal-icon-greyed flex-shrink-0"></i>
            </div>
            <div class="text-truncate goal-title">{{ event.award.context.title }}</div>
          </div>
          <award-widget
            :prevent-image-viewer="true"
            class="pb-3 award-widget"
            v-if="event.award"
            :award="event.award"
          />
          <reactions-list
            class="event-content"
            :event="event"
            @reacted="updateReactionSummary({ event, reactionSummary: $event })"
            v-if="!isCommentedEvent(event.type)"
          />
        </div>
      </div>
    </div>
  </with-gallery>
</template>

<script>
import AwardWidget from '@/components/award/AwardWidget'
import CheckinValues from '@/components/CheckinValues'
import EventTime from '@/components/EventTime'
import EventTimelinePointer from '@/components/EventTimelinePointer'
import OwnershipChangeEvent from '@/components/OwnershipChangeEvent'
import GoalEventWithComment from '@/components/goal/GoalEventWithComment'
import GoalEventActions from '@/components/GoalEventActions'
import ReactionsList from '@/components/reactions/ReactionsList'
import UserShort from '@/components/UserShort'
import WithGallery from '@/components/WithGallery'
import { AWARD_TYPE } from '@/constants/awards'
import { MIME_TYPE, VARIANT_MAP } from '@/constants/common'
import { ENTITY_TYPE } from '@/constants/entities'
import { GOAL_EVENT_TYPE } from '@/constants/goal'
import { COMMENT_FEED } from '@/constants/activity'
import getCheckinEventImpact from '@/utils/getCheckinEventImpact'
import getEventAwardContext from '@/utils/getEventAwardContext'
import getOutcomeLabel from '@/utils/getOutcomeLabel'
import URLify from '@/utils/URLify'
import * as R from 'ramda'

/**
 * This component renders goal's events
 * It supports v-model over *events* array
 *
 */
export default {
  name: 'GoalEvents',
  components: {
    UserShort,
    CheckinValues,
    AwardWidget,
    EventTimelinePointer,
    GoalEventActions,
    ReactionsList,
    WithGallery,
    EventTime,
    GoalEventWithComment,
    OwnershipChangeEvent
  },
  props: {
    value: {},
    goalId: [String, Number],
    mentionableUsers: Array,
    /**
     * context in which events are being shown
     */
    context: {
      default: ENTITY_TYPE.GOAL // goal | kr
    },
    /**
     * this contains the id of the event whose comment section should be expanded on load
     */
    eventToExpand: {},
    hideReply: Boolean,
    selectedActivityFilter: {}
  },
  data() {
    return {
      MIME_TYPE,
      GOAL_EVENT_TYPE,
      ENTITY_TYPE,
      VARIANT_MAP
    }
  },
  computed: {
    goal() {
      return this.$store.getters.goalById(this.goalId)
    },
    userId() {
      return R.path(['getters', 'user', 'id'], this.$store)
    },
    events() {
      return this.value
    },
    eventsToShow() {
      if (this.selectedActivityFilter && this.selectedActivityFilter.id === COMMENT_FEED) {
        const commentEvents = [GOAL_EVENT_TYPE.COMMENT, GOAL_EVENT_TYPE.AWARD]

        return this.events.filter(x => commentEvents.includes(x.type) && x.comment !== '')
      }
      return this.events
    }
  },
  methods: {
    URLify,
    getEventImpact(event) {
      const kr = this.$store.getters.keyResultById(event.keyResultId)
      return getCheckinEventImpact(event.checkin, kr)
    },
    onCommentUpdate(event, comment) {
      const eventIndex = R.findIndex(R.propEq('id', event.id), this.events)
      const events = R.assocPath([eventIndex, 'comment'], comment, this.events)
      this.$emit('input', events)
    },
    onCheckinUpdate(event, args) {
      const eventIndex = R.findIndex(R.propEq('id', event.id), this.events)
      let events = R.assocPath([eventIndex, 'comment'], args.comment, this.events)
      events = R.assocPath([eventIndex, 'checkin'], args.checkin, events)
      this.$emit('input', events)
      this.$emit('checkin-updated')
    },
    getKrSnapshot(event) {
      const checkin = this.getCheckinFromEvent(event)
      let snapshot = R.prop('keyResultSnapshot', event)
      if (!snapshot) {
        snapshot = R.path(['award', 'context', 'keyResultSnapshot'], event)
      }
      if (!snapshot) {
        snapshot = this.prepareFakeKrSnapshot(event)
      } else {
        // use current from checkin since current in keyResultSnapshot gets reset to lower limit on spill
        snapshot.current = checkin.newCurrent
      }
      return snapshot
    },
    /**
     * This function is used when keyResultSnapshot is not present
     */
    prepareFakeKrSnapshot(event) {
      const kr = this.$store.getters.keyResultById(event.keyResultId) || {}
      return {
        keyResultId: kr.id,
        current: event.checkin && event.checkin.newCurrent,
        end: kr.end
      }
    },
    kr({ keyResultId }) {
      if (this.goal) {
        return this.goal.keyResults.filter(kr => kr.id === keyResultId)[0]
      } else {
        return null
      }
    },
    isCommentedEvent(type) {
      return [GOAL_EVENT_TYPE.CHECKIN, GOAL_EVENT_TYPE.COMMENT, GOAL_EVENT_TYPE.CLOSED].includes(
        type
      )
    },
    getKrTitle(krId) {
      const kr = this.$store.getters.keyResultById(krId)
      return kr && kr.title
    },
    getGoalTitle(goalId) {
      const goal = this.$store.getters.goalById(goalId)
      return goal?.title
    },
    isAwardableEvent(event) {
      return [GOAL_EVENT_TYPE.CHECKIN, GOAL_EVENT_TYPE.COMMENT].includes(event.type)
    },
    isGoalEvent(event) {
      return event.type === GOAL_EVENT_TYPE.COMMENT && !event.keyResultId
    },
    getAwardContext(event) {
      if (this.isAwardableEvent(event)) {
        const contextTitle = this.isGoalEvent(event)
          ? this.getGoalTitle(event.goalId)
          : this.getKrTitle(event.keyResultId)
        return getEventAwardContext(event, ENTITY_TYPE.GOAL, contextTitle)
      } else {
        return {}
      }
    },
    isKrEvent(event) {
      const awardType = R.path(['award', 'type'], event)
      const eventType = event.type
      const isKrCommentEvent = eventType === GOAL_EVENT_TYPE.COMMENT && event.keyResultId
      return (
        eventType === GOAL_EVENT_TYPE.CHECKIN ||
        awardType === AWARD_TYPE.KEY_RESULT ||
        isKrCommentEvent
      )
    },
    getTimelinePointerType(event) {
      if (event.checkin) {
        return event.checkin.newStatus
      } else if (event.award) {
        return 'award'
      }
    },
    /**
     * returns name of the team to which kr/goal related to event belongs to.
     * For org goals, it returns null
     * @param event
     */
    getEventTeamName(event) {
      if (event.type === GOAL_EVENT_TYPE.CHECKIN) {
        const goal = this.$store.getters.goalById(event.goalId)
        const teamId = goal?.teamId
        if (teamId) {
          return this.$store.getters.teamById(teamId).name
        }
        return null
      }
    },
    getCheckinFromEvent(event) {
      return event.checkin || R.path(['award', 'context', 'checkin'], event)
    },
    isGoalAward(event) {
      return R.pathEq(['award', 'type'], AWARD_TYPE.GOAL, event)
    },
    getDisplayName(userId) {
      return this.$store.getters.userById(userId)?.displayName
    },
    onEventDeletion(event) {
      const events = this.events.filter(e => e.id !== event.id)
      this.$emit('input', events)
    },
    updateReactionSummary({ event, reactionSummary }) {
      const eventIndex = R.findIndex(R.propEq('id', event.id), this.events)
      const events = R.assocPath([eventIndex, 'reactionSummary'], reactionSummary, this.events)
      this.$emit('input', events)
    },
    getEntityName(event) {
      return event.keyResultId ? 'Key result' : 'Goal'
    },
    getDescriptionTextChange(event) {
      if (event.changeType == 'added') {
        return ' added a description'
      } else if (event.changeType === 'updated') {
        return ' updated the description'
      } else if (event.changeType === 'removed') {
        return ' removed the description'
      }
    },
    getKrFromEvent(event) {
      return this.$store.getters.keyResultById(event.keyResultId)
    },
    getOutcomeLabel
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/goals';
@import '~@/styles/errors';
@import '~@/styles/mixins';
@import '~@/styles/styleConfigurations';

.goal-event {
  font-size: 0.9375rem;
  border-left: 1px dashed $borderPrimary;
  border-bottom: 1px solid $borderSecondary;

  .content {
    padding-left: 20px;
    padding-top: 16px;
    padding-right: 12px;
  }

  .selector:focus + .content {
    background-color: $selectionColor;
  }

  .goal-event-header {
    padding-bottom: 8px;
  }

  /deep/ .goal-event-header .user-name {
    font-weight: 600;
  }

  .goal-event-time {
    color: $secondary;
    font-weight: normal;
  }

  .comment-save-menu {
    padding-left: 34px;
    bottom: 1px;

    button {
      font-size: 0.875rem;
    }
  }

  .entity-icon-container {
    width: 40px;
    padding-top: 5px;
    display: flex;
    justify-content: center;
  }

  .arrow-up {
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 6px solid $active;
    margin-right: 4px;
  }

  .arrow-down {
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid $error;
    margin-right: 4px;
  }

  .green {
    color: $active;
  }

  .red {
    color: $error;
  }
}

.kr-icon-greyed {
  transform: scale(0.8);
}

.goal-icon-greyed {
  @include dimension(10px, 10px);
  background-size: contain;
}

.goal-title {
  margin-left: 8px;
}

.award-widget {
  padding-left: 50px;
}

.actor-detail {
  align-items: baseline;
  margin-top: 1px;
  flex-shrink: 0;
}

.goal-events {
  margin-top: 25px;
}

.actor-detail {
  vertical-align: bottom;
}

.kr-info {
  position: initial;
}

.kr-title {
  @include lineClamp();
}

.kr-title {
  position: initial;
}
.field-value {
  font-weight: bold;
  text-transform: capitalize;
}

.event-status {
  padding: 7px 22px;
}

/deep/ .event-content {
  padding-left: 50px;
}

.event-action-wrapper {
  opacity: 0;
  transition: opacity ease-in 150ms;
}

.goal-event:hover {
  .event-action-wrapper,
  /deep/ .link-icon {
    opacity: 1;
  }
}

/deep/ .link-icon {
  vertical-align: middle;
  opacity: 0;
  transition: opacity ease-in 150ms;
}

@include media-breakpoint-down(md) {
  .checkin-info {
    flex-direction: column;
  }
  .kr-info {
    max-width: initial;
  }
}
</style>
